import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { translateSignal, TranslocoModule } from '@jsverse/transloco';
import { AuthService } from '../../../../core/services/auth.service';
import { ConfirmationService, MenuItem } from 'primeng/api';

import { AvatarModule } from 'primeng/avatar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MenuModule } from 'primeng/menu';
import { IconsService } from '@rieaya-core';
import { LaundryUserService } from '../../../../core/services/laundry-user.service';
import { User } from '../../../../core/interfaces/user';
import { RouterLinkFactory } from '../../../../core/router/router-link-factory';
import { environment } from '../../../../../environments/environment';
import { BaseResponse } from '../../../../core/interfaces/base-response';
import {
  LANG,
  LAUNDRY,
  LAUNDRY_MANAGER,
  TOKEN,
  USER_DATA,
} from '../../../../core/utils/constant';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-avatar',
  imports: [
    AvatarModule,
    MenuModule,
    RouterModule,
    CommonModule,
    ConfirmDialogModule,
    TranslocoModule,
  ],
  templateUrl: './user-avatar.component.html',
  styleUrl: './user-avatar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAvatarComponent {
  links = inject(RouterLinkFactory);
  icons = inject(IconsService);
  private laundryUserService = inject(LaundryUserService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  user = signal<User>(JSON.parse(localStorage.getItem(USER_DATA) || ''));
  role = JSON.parse(localStorage.getItem(USER_DATA) || '')?.role;
  items: MenuItem[] | undefined;
  LAUNDRY_MANAGER = LAUNDRY_MANAGER;

  constructor() {
    const translations = {
      profile: translateSignal('shared/profile'),
      logout: translateSignal('shared/logout'),
      laundryDetails: translateSignal('shared/laundryDetails'),
      confirm: translateSignal('shared/confirm'),
      confirmMessage: translateSignal('shared/confirmMessage'),
      accept: translateSignal('shared/yes'),
      reject: translateSignal('shared/no'),
    };

    effect(() => {
      this.items = [
        ...(this.role === LAUNDRY_MANAGER
          ? [
              {
                label: translations.profile(),
                icon: this.icons.user,
                route: this.links.profile(),
              },
              {
                label: translations.laundryDetails(),
                icon: this.icons.penToSquare,
                route: this.links.laundryDetails(),
              },
            ]
          : []),
        {
          label: translations.logout(),
          icon: this.icons.signOut,
          command: () => {
            this.confirmationService.confirm({
              message: translations.confirmMessage(),
              header: translations.confirm(),
              icon: this.icons.exclamationTriangle,
              accept: () => {
                this.logout();
              },
              acceptButtonStyleClass:
                '!bg-red-500 !hover:bg-red-600 !border-red-500',
              acceptLabel: translations.accept(),
              rejectLabel: translations.reject(),
            });
          },
        },
      ];
    });

    this.getUserData();
  }

  getUserData() {
    this.laundryUserService
      .getCurrentUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: BaseResponse<User>) => {
          const sessionUserData = JSON.parse(
            localStorage.getItem(USER_DATA) || '{}'
          );

          this.user.set(response.data);
          this.user.update((current) => ({
            ...current,
            photo: `${environment.base_url}${response.data.photo}`,
          }));

          localStorage.setItem(
            USER_DATA,
            JSON.stringify({
              ...response.data,
              role: this.role,
              onlyCollectionPoint:
                sessionUserData.onlyCollectionPoint ??
                response.data.onlyCollectionPoint ??
                false,
            })
          );
        },
        error: (err) => console.error('Error fetching user data:', err),
      });
  }

  logout() {
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          localStorage.removeItem(TOKEN);
          localStorage.removeItem(USER_DATA);
          localStorage.removeItem(LANG);
          localStorage.removeItem(LAUNDRY);
          window.location.href = environment.LoginSSOROP;
        },
      });
  }
}
